import { useForm } from '@tanstack/react-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { Button, Label } from '@workspace/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  PasswordInput,
  errorToFormIssue,
  signInEmail,
} from '@workspace/ui';

import { AuthShell } from '../components/auth-shell.tsx';
import { enrollDestination } from '../lib/enroll.ts';
import { auth } from '../firebase.tsx';

const schema = z.object({
  email: z.string().min(1, { error: 'Email is required' }).email(),
  password: z.string().min(1, { error: 'Password is required' }),
});

export default function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const course = searchParams.get('course');

  const form = useForm({
    defaultValues: { email: '', password: '' },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await signInEmail(auth, value.email, value.password);
          await navigate(enrollDestination(course));
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  const signUpHref = course ? `/signup?course=${course}` : '/signup';

  return (
    <AuthShell
      title="Sign in"
      subtitle={
        <span className="flex gap-x-1">
          Don&apos;t have an account?
          <Link to={signUpHref} className="text-primary font-medium">
            Create one
          </Link>
        </span>
      }
    >
      <form
        className="mt-6 space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="email"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="email">Email</Label>
              <EmailInput
                id="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="password"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <Link
                to="/forgot-password"
                className="text-primary text-sm font-medium"
              >
                Forgot your password?
              </Link>
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit} className="w-full">
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
          )}
        />
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
      </form>
    </AuthShell>
  );
}
